import React, { CSSProperties, useCallback } from 'react';

import styled from 'styled-components';

import { PopoverProps } from '@mui/material/Popover/Popover';
import {
  Button,
  Popover,
  PopoverCloseButton,
  Stack,
  Typography,
} from 'components';

import { useTranslation } from 'third-party';

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  padding: 2.2rem 2.3rem;
  z-index: 1;
  gap: 1.6rem;
  min-width: 0;
`;

const Title = styled(Typography)`
  display: inline-block;
  line-height: 100%;
  color: ${props => props.theme.custom.palette.primary900};
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  vertical-align: middle;
  padding-right: 1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.6rem;
  flex-shrink: 0;
  flex-wrap: nowrap;
`;

const Content = styled(Stack)<{ width?: string }>`
  flex-grow: 1;
  overflow: scroll;
  padding: 2.2rem 2.3rem 2.7rem;
  box-sizing: border-box;
  width: ${props => props.width || '45rem'};
`;

type Props = PopoverProps & {
  open: boolean;
  children: React.ReactElement | React.ReactElement[];
  header?: string;
  isPending?: boolean;
  isDisabled?: boolean;
  cancelEnabled?: boolean;
  closeEnabled?: boolean;
  headerEnabled?: boolean;
  isConfirmDisabled?: boolean;
  cancelLabel?: string;
  confirmLabel?: string;
  blurBackgroundSize?: number;
  onConfirm?: () => void;
  onClose: () => void;
  onCancel?: () => void;
  width?: string;
  contentWrapperStyle?: CSSProperties;
};

export const Modal: React.FC<Props> = ({
  children,
  header,
  width,
  isPending = false,
  isDisabled = false,
  closeEnabled = true,
  cancelEnabled = true,
  headerEnabled = true,
  isConfirmDisabled = false,
  cancelLabel,
  confirmLabel,
  blurBackgroundSize = 0,
  onConfirm,
  onClose,
  onCancel,
  open,
  contentWrapperStyle,
  ...props
}) => {
  const { t } = useTranslation();
  const handleCancelClick = useCallback(() => {
    onCancel?.();
    onClose();
  }, [onCancel, onClose]);
  return (
    <Popover
      anchorEl={document.body}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      open={open}
      style={
        blurBackgroundSize
          ? { backdropFilter: `blur(${blurBackgroundSize}px)` }
          : {}
      }
      slotProps={{
        paper: {
          style: {
            overflow: 'visible',
          },
        },
      }}
      {...props}
    >
      {closeEnabled && <PopoverCloseButton onClose={onClose} />}
      {headerEnabled && (
        <Header>
          {header && <Title variant="h3">{header}</Title>}
          <ActionButtons>
            {cancelEnabled && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelClick}
                disabled={isPending}
              >
                {cancelLabel || t('common.cancel')}
              </Button>
            )}
            {confirmLabel && (
              <Button
                loading={isPending}
                variant="contained"
                color="primary"
                disabled={isDisabled || isConfirmDisabled}
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
            )}
          </ActionButtons>
        </Header>
      )}
      <Content style={contentWrapperStyle} width={width}>
        {children}
      </Content>
    </Popover>
  );
};
